import React, { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { AnalyticsApi } from '../../services/api/AnalyticsApi';
import {
  MetricCard,
  LoadingChart,
  EmptyState,
  DateRangePicker,
  ExportButton
} from '../../components/analytics/SharedAnalyticsComponents';
import PdfExportButton from '../../components/analytics/PdfExportButton';
import { ReportGenerator } from '../../utils/ReportGenerator';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Cell
} from 'recharts';

const COLORS = ['#007bff', '#28a745', '#ffc107', '#dc3545', '#17a2b8', '#6f42c1', '#20c997', '#fd7e14'];

// Common event types available for funnel steps
const COMMON_EVENT_TYPES = [
  'PAGE_VIEW', 'DOCUMENT_VIEW', 'DOCUMENT_UPLOADED',
  'CHAT_PROMPT_SENT', 'SEARCH_PERFORMED',
  'QUIZ_STARTED', 'QUIZ_COMPLETED',
  'FLASHCARD_SESSION_STARTED', 'FLASHCARD_SESSION_COMPLETED',
  'SUBSCRIPTION_STARTED', 'SUBSCRIPTION_UPGRADED',
  'AUDIO_GENERATION_STARTED', 'AUDIO_PLAYBACK_STARTED',
  'ENDEAVOR_CREATED', 'SYLLABUS_GENERATED'
];

/**
 * ConversionGoals — Manage conversion goals and track funnel metrics.
 * Wires: getConversionGoals, createConversionGoal, deleteConversionGoal, getGoalConversions
 */
export default function ConversionGoals() {
  const { user } = useAuth();
  const isAdmin = user?.roles?.some(r => r.roleName === 'admin');
  const [activeTab, setActiveTab] = useState('goals');

  if (!isAdmin) {
    return (
      <div className="settings-container">
        <h2>Conversion Goals</h2>
        <EmptyState message="Admin access required" icon="🔒" />
      </div>
    );
  }

  const tabs = [
    { id: 'goals', label: '🎯 Goals Overview' },
    { id: 'create', label: '➕ Create Goal' }
  ];

  return (
    <div className="settings-container">
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ margin: 0 }}>🎯 Conversion Goals</h2>
        <p style={{ fontSize: '.85rem', color: 'var(--text-muted)', margin: '.25rem 0 0 0' }}>
          Define and track multi-step conversion funnels
        </p>
      </div>

      {/* Tab Bar */}
      <div style={{ display: 'flex', gap: '.5rem', marginBottom: '1.5rem', borderBottom: '2px solid var(--border-light)', paddingBottom: '.5rem' }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '.5rem 1rem',
              background: activeTab === tab.id ? '#007bff' : 'transparent',
              color: activeTab === tab.id ? '#fff' : 'inherit',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: activeTab === tab.id ? 600 : 400,
              fontSize: '.85rem'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'goals' && <GoalsOverviewTab />}
      {activeTab === 'create' && <CreateGoalTab onCreated={() => setActiveTab('goals')} />}
    </div>
  );
}

// ─── Goals Overview Tab ─────────────────────────────────────────────

function GoalsOverviewTab() {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [conversions, setConversions] = useState(null);
  const [convLoading, setConvLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    from: new Date(Date.now() - 30 * 86400000).toISOString(),
    to: new Date().toISOString()
  });

  const loadGoals = useCallback(async () => {
    setLoading(true);
    try {
      const data = await AnalyticsApi.getConversionGoals();
      setGoals(data || []);
    } catch (err) {
      console.error('Failed to load goals:', err);
      setGoals([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadGoals(); }, [loadGoals]);

  const handleSelectGoal = useCallback(async (goal) => {
    setSelectedGoal(goal);
    setConvLoading(true);
    try {
      const data = await AnalyticsApi.getGoalConversions(goal.id, {
        from: dateRange.from,
        to: dateRange.to
      });
      setConversions(data);
    } catch (err) {
      console.error('Failed to load conversions:', err);
      setConversions(null);
    } finally {
      setConvLoading(false);
    }
  }, [dateRange]);

  const handleDeleteGoal = useCallback(async (goalId) => {
    if (!window.confirm('Are you sure you want to deactivate this goal?')) return;
    try {
      await AnalyticsApi.deleteConversionGoal(goalId);
      setGoals(prev => prev.filter(g => g.id !== goalId));
      if (selectedGoal?.id === goalId) {
        setSelectedGoal(null);
        setConversions(null);
      }
    } catch (err) {
      console.error('Failed to delete goal:', err);
    }
  }, [selectedGoal]);

  // Reload conversions when date range changes
  useEffect(() => {
    if (selectedGoal) {
      handleSelectGoal(selectedGoal);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  if (loading) return <LoadingChart />;

  if (goals.length === 0) {
    return (
      <EmptyState
        message="No conversion goals defined yet. Create your first goal!"
        icon="🎯"
      />
    );
  }

  return (
    <div>
      {/* Date Range Picker */}
      <div style={{ marginBottom: '1rem' }}>
        <DateRangePicker
          from={dateRange.from}
          to={dateRange.to}
          onChange={setDateRange}
        />
      </div>

      {/* Goals List */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {goals.map(goal => (
          <div
            key={goal.id}
            onClick={() => handleSelectGoal(goal)}
            style={{
              padding: '1rem',
              border: selectedGoal?.id === goal.id ? '2px solid #007bff' : '1px solid var(--border-light)',
              borderRadius: '8px',
              cursor: 'pointer',
              background: selectedGoal?.id === goal.id ? 'rgba(0,123,255,.05)' : 'var(--bg-card)',
              transition: 'all .2s ease'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h4 style={{ margin: '0 0 .25rem 0' }}>🎯 {goal.name}</h4>
                {goal.description && (
                  <p style={{ fontSize: '.8rem', color: 'var(--text-muted)', margin: '0 0 .5rem 0' }}>
                    {goal.description}
                  </p>
                )}
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); handleDeleteGoal(goal.id); }}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#dc3545', fontSize: '1.1rem', padding: '.25rem'
                }}
                title="Deactivate goal"
              >
                ✕
              </button>
            </div>
            <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
              {(goal.funnelSteps || []).map((step, i) => (
                <span
                  key={i}
                  style={{
                    fontSize: '.7rem',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    background: COLORS[i % COLORS.length] + '22',
                    color: COLORS[i % COLORS.length],
                    fontWeight: 500
                  }}
                >
                  {i + 1}. {step}
                </span>
              ))}
            </div>
            {goal.targetRate > 0 && (
              <div style={{ fontSize: '.75rem', color: 'var(--text-muted)', marginTop: '.5rem' }}>
                Target: {goal.targetRate}%
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Selected Goal Detail */}
      {selectedGoal && (
        <GoalDetail
          goal={selectedGoal}
          conversions={conversions}
          loading={convLoading}
          dateRange={dateRange}
        />
      )}
    </div>
  );
}

// ─── Goal Detail Panel ──────────────────────────────────────────────

function GoalDetail({ goal, conversions, loading, dateRange }) {
  if (loading) return <LoadingChart />;
  if (!conversions) return null;

  const { funnelMetrics, overallConversionRate, totalStarted, totalConverted, targetRate } = conversions;

  const chartData = (funnelMetrics || []).map((step, i) => ({
    name: step.eventType?.replace(/_/g, ' ') || `Step ${i + 1}`,
    users: step.count,
    dropOff: Number(step.dropOffRate || 0)
  }));

  const meetsTarget = targetRate > 0 && overallConversionRate >= targetRate;

  return (
    <div style={{ background: 'var(--bg-card)', borderRadius: '8px', padding: '1.25rem', border: '1px solid var(--border-light)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3 style={{ margin: 0 }}>📊 {goal.name} — Conversion Detail</h3>
        <div style={{ display: 'flex', gap: '.5rem' }}>
          <ExportButton data={chartData} filename={`goal-${goal.id}-conversions`} format="csv" />
          <PdfExportButton onGenerate={async () => {
            const report = new ReportGenerator(`Conversion Goal: ${goal.name}`, dateRange);
            report.addMetrics([
              { label: 'Total Started', value: String(totalStarted || 0) },
              { label: 'Total Converted', value: String(totalConverted || 0) },
              { label: 'Conversion Rate', value: `${overallConversionRate || 0}%` },
              ...(targetRate > 0 ? [{ label: 'Target', value: `${targetRate}%`, sublabel: meetsTarget ? 'Met' : 'Not met' }] : []),
            ]);
            report.addSectionHeading('Funnel Steps');
            report.addTable(
              ['Step', 'Users', 'Drop-off %'],
              chartData.map(d => [d.name, d.users, `${(d.dropOff * 100).toFixed(1)}%`])
            );
            report.download(`goal-${goal.id}-conversions`);
          }} />
        </div>
      </div>

      {/* Summary Metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <MetricCard label="Total Started" value={totalStarted?.toLocaleString() || '0'} />
        <MetricCard label="Total Converted" value={totalConverted?.toLocaleString() || '0'} />
        <MetricCard
          label="Conversion Rate"
          value={`${overallConversionRate || 0}%`}
          trend={meetsTarget ? 'up' : targetRate > 0 ? 'down' : null}
        />
        {targetRate > 0 && (
          <MetricCard
            label="Target"
            value={`${targetRate}%`}
            trend={meetsTarget ? 'met' : 'unmet'}
          />
        )}
      </div>

      {/* Funnel Bar Chart */}
      {chartData.length > 0 && (
        <div>
          <h4 style={{ margin: '0 0 .75rem 0', fontSize: '.9rem' }}>Funnel Steps</h4>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-20} textAnchor="end" height={60} />
              <YAxis />
              <Tooltip
                formatter={(value, name) => {
                  if (name === 'users') return [value.toLocaleString(), 'Unique Users'];
                  if (name === 'dropOff') return [`${value}%`, 'Drop-off'];
                  return [value, name];
                }}
              />
              <Legend />
              <Bar dataKey="users" name="Unique Users" radius={[4, 4, 0, 0]}>
                {chartData.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Drop-off Table */}
      {funnelMetrics && funnelMetrics.length > 1 && (
        <div style={{ marginTop: '1rem' }}>
          <h4 style={{ margin: '0 0 .5rem 0', fontSize: '.9rem' }}>Step-by-Step Drop-off</h4>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border-light)' }}>
                <th style={{ textAlign: 'left', padding: '.5rem' }}>Step</th>
                <th style={{ textAlign: 'left', padding: '.5rem' }}>Event</th>
                <th style={{ textAlign: 'right', padding: '.5rem' }}>Users</th>
                <th style={{ textAlign: 'right', padding: '.5rem' }}>Drop-off</th>
              </tr>
            </thead>
            <tbody>
              {funnelMetrics.map((step, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--border-light)' }}>
                  <td style={{ padding: '.5rem' }}>
                    <span style={{
                      display: 'inline-block', width: '22px', height: '22px',
                      borderRadius: '50%', background: COLORS[i % COLORS.length],
                      color: '#fff', textAlign: 'center', lineHeight: '22px',
                      fontSize: '.75rem', fontWeight: 600
                    }}>
                      {i + 1}
                    </span>
                  </td>
                  <td style={{ padding: '.5rem' }}>{step.eventType}</td>
                  <td style={{ padding: '.5rem', textAlign: 'right', fontWeight: 600 }}>
                    {step.count?.toLocaleString()}
                  </td>
                  <td style={{ padding: '.5rem', textAlign: 'right', color: step.dropOffRate > 50 ? '#dc3545' : step.dropOffRate > 25 ? '#ffc107' : '#28a745' }}>
                    {i === 0 ? '—' : `${step.dropOffRate}%`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Create Goal Tab ────────────────────────────────────────────────

function CreateGoalTab({ onCreated }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [funnelSteps, setFunnelSteps] = useState(['']);
  const [targetRate, setTargetRate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const addStep = () => setFunnelSteps(prev => [...prev, '']);
  const removeStep = (index) => setFunnelSteps(prev => prev.filter((_, i) => i !== index));
  const updateStep = (index, value) => {
    setFunnelSteps(prev => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validSteps = funnelSteps.filter(s => s.trim());
    if (validSteps.length < 2) {
      setError('At least 2 funnel steps are required');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await AnalyticsApi.createConversionGoal({
        name: name.trim(),
        description: description.trim() || null,
        eventType: validSteps[validSteps.length - 1], // target = last step
        funnelSteps: validSteps,
        targetRate: targetRate ? parseFloat(targetRate) : 0
      });
      onCreated();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Pre-built templates
  const templates = [
    {
      name: 'Signup → First Document',
      steps: ['PAGE_VIEW', 'DOCUMENT_UPLOADED', 'DOCUMENT_OPENED', 'CHAT_PROMPT_SENT'],
      target: 25
    },
    {
      name: 'Document → Quiz Completion',
      steps: ['DOCUMENT_OPENED', 'QUIZ_STARTED', 'QUIZ_COMPLETED'],
      target: 60
    },
    {
      name: 'Free → Paid Conversion',
      steps: ['PAGE_VIEW', 'DOCUMENT_OPENED', 'SUBSCRIPTION_STARTED'],
      target: 5
    }
  ];

  const applyTemplate = (template) => {
    setName(template.name);
    setFunnelSteps(template.steps);
    setTargetRate(String(template.target));
  };

  return (
    <div>
      {/* Templates */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h4 style={{ margin: '0 0 .5rem 0', fontSize: '.9rem' }}>Quick Templates</h4>
        <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
          {templates.map((tmpl, i) => (
            <button
              key={i}
              onClick={() => applyTemplate(tmpl)}
              style={{
                padding: '.4rem .8rem',
                border: '1px solid var(--border-light)',
                borderRadius: '6px',
                background: 'var(--bg-card)',
                cursor: 'pointer',
                fontSize: '.8rem'
              }}
            >
              📋 {tmpl.name}
            </button>
          ))}
        </div>
      </div>

      {/* Create Form */}
      <form onSubmit={handleSubmit} style={{ background: 'var(--bg-card)', borderRadius: '8px', padding: '1.25rem', border: '1px solid var(--border-light)' }}>
        <h4 style={{ margin: '0 0 1rem 0' }}>New Conversion Goal</h4>

        {error && (
          <div style={{ padding: '.75rem', background: '#dc354522', color: '#dc3545', borderRadius: '6px', marginBottom: '1rem', fontSize: '.85rem' }}>
            {error}
          </div>
        )}

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', fontSize: '.85rem', fontWeight: 600, marginBottom: '.25rem' }}>Goal Name *</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g., Signup to First Quiz"
            required
            style={{ width: '100%', padding: '.5rem', borderRadius: '6px', border: '1px solid var(--border-light)', boxSizing: 'border-box' }}
          />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', fontSize: '.85rem', fontWeight: 600, marginBottom: '.25rem' }}>Description</label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Optional description of what this goal tracks"
            rows={2}
            style={{ width: '100%', padding: '.5rem', borderRadius: '6px', border: '1px solid var(--border-light)', boxSizing: 'border-box', resize: 'vertical' }}
          />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', fontSize: '.85rem', fontWeight: 600, marginBottom: '.5rem' }}>
            Funnel Steps * (min 2)
          </label>
          {funnelSteps.map((step, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '.5rem' }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: '24px', height: '24px', borderRadius: '50%',
                background: COLORS[i % COLORS.length], color: '#fff',
                fontSize: '.75rem', fontWeight: 600, flexShrink: 0
              }}>
                {i + 1}
              </span>
              <select
                value={step}
                onChange={e => updateStep(i, e.target.value)}
                style={{ flex: 1, padding: '.5rem', borderRadius: '6px', border: '1px solid var(--border-light)' }}
              >
                <option value="">Select event type...</option>
                {COMMON_EVENT_TYPES.map(et => (
                  <option key={et} value={et}>{et}</option>
                ))}
              </select>
              {funnelSteps.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeStep(i)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc3545', fontSize: '1.1rem' }}
                >
                  ✕
                </button>
              )}
              {i < funnelSteps.length - 1 && (
                <span style={{ color: 'var(--text-muted)', fontSize: '1.2rem' }}>→</span>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={addStep}
            style={{
              padding: '.35rem .75rem',
              border: '1px dashed var(--border-light)',
              borderRadius: '6px',
              background: 'transparent',
              cursor: 'pointer',
              fontSize: '.8rem',
              marginTop: '.25rem'
            }}
          >
            + Add Step
          </button>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', fontSize: '.85rem', fontWeight: 600, marginBottom: '.25rem' }}>Target Conversion Rate (%)</label>
          <input
            type="number"
            value={targetRate}
            onChange={e => setTargetRate(e.target.value)}
            min="0"
            max="100"
            step="0.1"
            placeholder="e.g., 25"
            style={{ width: '120px', padding: '.5rem', borderRadius: '6px', border: '1px solid var(--border-light)' }}
          />
        </div>

        <button
          type="submit"
          disabled={saving || !name.trim() || funnelSteps.filter(s => s.trim()).length < 2}
          style={{
            padding: '.6rem 1.5rem',
            background: '#007bff',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: 600,
            opacity: saving ? 0.6 : 1
          }}
        >
          {saving ? 'Creating…' : '🎯 Create Goal'}
        </button>
      </form>
    </div>
  );
}
